import { useState } from "react";
import {
  ArrowRightLeft,
  ExternalLink,
  MessageSquare,
  TriangleAlert,
  User,
} from "lucide-react";
import { contactLabel, formatPhone } from "../analysis/analysisLabels";

const idleLabel = (days) => {
  if (days === null || days === undefined) return "—";
  if (days === 0) return "hoje";
  return days === 1 ? "1 dia" : `${days} dias`;
};

const ConversationCard = ({ card, stages, busy, onOpen, onMove }) => {
  const name = contactLabel(card.contact);
  const phone = formatPhone(card.contact?.phoneNumber);

  return (
    <div
      className={`rounded-lg border bg-white p-3 shadow-sm transition-colors dark:bg-gray-900 ${
        card.stalled
          ? "border-amber-300 dark:border-amber-800"
          : "border-gray-200 dark:border-gray-700"
      } ${card.closed ? "opacity-60" : ""}`}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
          {name}
        </p>
        {card.stalled && (
          <TriangleAlert
            className="h-4 w-4 shrink-0 text-amber-500"
            title={`Sem mensagem há ${idleLabel(card.idleDays)}`}
          />
        )}
      </div>

      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        {phone && phone !== name && `${phone} · `}
        sem mensagem há {idleLabel(card.idleDays)}
        {card.daysInStage !== null && ` · ${idleLabel(card.daysInStage)} nesta etapa`}
      </p>

      {card.tags?.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {card.tags.slice(0, 3).map((tag) => (
            <span
              key={tag.id}
              className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ backgroundColor: tag.backgroundColor, color: tag.textColor }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
          {card.assignedUsers?.length > 0 ? (
            <>
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {card.assignedUsers.map((user) => user.name).join(", ")}
              </span>
            </>
          ) : (
            <span className="text-amber-600 dark:text-amber-400">Sem responsável</span>
          )}
        </span>

        <span className="flex shrink-0 items-center gap-1 text-[11px] text-gray-400">
          <MessageSquare className="h-3 w-3" />
          {card.messageCount}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1">
        <select
          value=""
          disabled={busy}
          onChange={(event) =>
            event.target.value && onMove(card.conversationId, event.target.value)
          }
          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 py-1 text-[11px] text-gray-700 outline-none focus:border-green-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          title="Mover para outra etapa"
        >
          <option value="">Mover para...</option>
          {stages.map((stage) => (
            <option key={stage} value={stage}>
              {stage}
            </option>
          ))}
          <option value="__none__">Sem etapa</option>
        </select>

        <button
          onClick={() => onOpen?.(card.conversationId)}
          className="rounded border border-gray-300 p-1 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          title="Abrir conversa"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

const StageColumn = ({ stage, stages, busyId, onOpen, onMove }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? stage.conversations : stage.conversations.slice(0, 8);

  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg bg-gray-100 p-3 dark:bg-gray-900/60">
      <div className="mb-3">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate font-semibold text-gray-900 dark:text-gray-100">
            {stage.name}
          </p>
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {stage.total}
          </span>
        </div>

        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {stage.stalled > 0 && (
            <span className="font-medium text-amber-600 dark:text-amber-400">
              {stage.stalled} parada{stage.stalled > 1 ? "s" : ""}
            </span>
          )}
          {stage.stalled > 0 && stage.avgDaysInStage !== null && " · "}
          {stage.avgDaysInStage !== null && `média ${stage.avgDaysInStage}d na etapa`}
          {stage.stalled === 0 && stage.avgDaysInStage === null && "sem alertas"}
        </p>
      </div>

      <div className="space-y-2">
        {stage.conversations.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400">Nenhuma conversa</p>
        ) : (
          visible.map((card) => (
            <ConversationCard
              key={card.conversationId}
              card={card}
              stages={stages}
              busy={busyId === card.conversationId}
              onOpen={onOpen}
              onMove={onMove}
            />
          ))
        )}
      </div>

      {stage.conversations.length > 8 && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 text-xs text-gray-600 underline transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {expanded
            ? "Mostrar menos"
            : `Ver mais ${stage.conversations.length - 8}`}
        </button>
      )}

      {stage.total > stage.conversations.length && (
        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          Exibindo {stage.conversations.length} de {stage.total}.
        </p>
      )}
    </div>
  );
};

const PipelineBoard = ({ pipeline, busyId, onOpen, onMove }) => {
  const stages = pipeline.stages.map((stage) => stage.name);
  const columns = [
    ...pipeline.stages,
    ...pipeline.orphanStages,
    ...(pipeline.withoutStage.total > 0 ? [pipeline.withoutStage] : []),
  ];

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-4">
        {columns.map((stage) => (
          <StageColumn
            key={stage.name}
            stage={stage}
            stages={stages}
            busyId={busyId}
            onOpen={onOpen}
            onMove={onMove}
          />
        ))}

        {columns.length === 0 && (
          <div className="w-full rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
            <ArrowRightLeft className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">
              Este funil ainda não tem etapas. Cadastre as etapas na aba ao lado.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineBoard;
